// src/pages/WatchModels.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { FaTools, FaSearch } from 'react-icons/fa';
import RepairsModal from '../components/RepairsModal';

function WatchModels() {
    const { brand: brandUrlParam } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedWatch, setSelectedWatch] = useState(null);
    const [repairsData, setRepairsData] = useState([]);
    const [showRepairsModal, setShowRepairsModal] = useState(false);
    const [isLoadingRepairs, setIsLoadingRepairs] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [hasOpenedModalFromUrl, setHasOpenedModalFromUrl] = useState(false);

    const getWatchDbBrand = useCallback((urlBrand) => {
        const lower = urlBrand?.toLowerCase();
        switch (lower) {
            case 'iwatch': return 'Apple';
            case 'samsung': return 'Samsung';
            case 'garmin': return 'Garmin';
            case 'fitbit': return 'Fitbit';
            case 'amazfit': return 'Amazfit';
            default: return urlBrand;
        }
    }, []);

    // Function to extract or determine watch release year
    const getWatchReleaseYear = useCallback((watchName, brand) => {
        const name = watchName.toLowerCase();
        
        // Watch model to year mapping for known models
        const watchYearMapping = {
            // Apple Watch models
            'iwatch series 10 (46mm)': 2024,
            'iwatch series 10 (42mm)': 2024,
            'iwatch series ultra 2 2023 (49mm)': 2023,
            'iwatch series ultra 2022 (49mm)': 2022,
            'iwatch series 9 (45mm)': 2023,
            'iwatch series 9 (41mm)': 2023,
            'iwatch series 8 (45mm)': 2022,
            'iwatch series 8 (41mm)': 2022,
            'iwatch series se (44mm) 2nd gen 2022': 2022,
            'iwatch series se (40mm) 2nd gen 2022': 2022,
            'iwatch series 7 (45mm)': 2021,
            'iwatch series 7 (44mm)': 2021,
            'iwatch series 7 (41mm)': 2021,
            'iwatch series se (44mm) (1st gen - 2020)': 2020,
            'iwatch series se (40mm) (1st gen - 2020)': 2020,
            'iwatch series 6 (44mm)': 2020,
            'iwatch series 6 (40mm)': 2020,
            'iwatch series 5 (44mm)': 2019,
            'iwatch series 5 (40mm)': 2019,
            'iwatch series 4 (44mm)': 2018,
            'iwatch series 4 (40mm)': 2018,
            'iwatch series 3 (42mm)': 2017,
            'iwatch series 3 (38mm)': 2017,
            'iwatch series 3 (42mm) gps + cellular': 2017,
            'iwatch series 3 38mm gps + cellular': 2017,
            'iwatch series 2 (42mm)': 2016,
            'iwatch series 2 (38mm)': 2016,
            'iwatch series 1 (42mm)': 2016,
            'iwatch series 1 (38mm)': 2016,
            
            // Samsung Galaxy Watch Series
            'galaxy watch 6 classic (47mm)': 2023,
            'galaxy watch 6 classic (43mm)': 2023,
            'galaxy watch 6 (44mm)': 2023,
            'galaxy watch 6 (40mm)': 2023,
            'galaxy watch 5 pro': 2022,
            'galaxy watch 5 (44mm)': 2022,
            'galaxy watch 5 (40mm)': 2022,
            'galaxy watch 4 classic (46mm)': 2021,
            'galaxy watch 4 classic (42mm)': 2021,
            'galaxy watch 4 (44mm)': 2021,
            'galaxy watch 4 (40mm)': 2021,
            'galaxy watch 3 (45mm)': 2020,
            'galaxy watch 3 (41mm)': 2020,
            'galaxy watch active 2': 2019,
            'galaxy watch active': 2019,
            'galaxy watch (46mm)': 2018,
            'galaxy watch (42mm)': 2018,
        };
        
        // Check exact match first
        const exactMatch = watchYearMapping[name];
        if (exactMatch) return exactMatch;
        
        // Try partial matching for variations
        for (const [modelName, year] of Object.entries(watchYearMapping)) {
            if (name.includes(modelName) || modelName.includes(name)) {
                return year;
            }
        }
        
        // Extract year from watch name (look for 4-digit years)
        const yearMatch = name.match(/\b(20[0-9]{2})\b/);
        if (yearMatch) {
            return parseInt(yearMatch[1]);
        }
        
        // Extract series numbers for Apple Watches
        if (brand === 'Apple') {
            const seriesMatch = name.match(/series\s*(\d+)/i);
            if (seriesMatch) {
                const series = parseInt(seriesMatch[1]);
                // Apple Watch Series mapping
                if (series >= 10) return 2024;
                if (series === 9) return 2023;
                if (series === 8) return 2022;
                if (series === 7) return 2021;
                if (series === 6) return 2020;
                if (series === 5) return 2019;
                if (series === 4) return 2018;
                if (series === 3) return 2017;
                if (series <= 2) return 2016;
            }
        }
        
        // Default to 2020 if year cannot be determined
        return 2020;
    }, []);

    // Function to sort watches by year (latest first)
    const sortWatchesByYear = useCallback((watches) => {
        return [...watches].sort((a, b) => {
            const yearA = getWatchReleaseYear(a.model || a.name, a.brand);
            const yearB = getWatchReleaseYear(b.model || b.name, b.brand);
            
            // Sort by year descending (latest first)
            if (yearB !== yearA) {
                return yearB - yearA;
            }
            
            // If same year, sort by name
            const nameA = (a.model || a.name).toLowerCase();
            const nameB = (b.model || b.name).toLowerCase();
            
            // For same year, put larger screens first
            const getSizePriority = (name) => {
                if (name.includes('49mm')) return 7;
                if (name.includes('47mm')) return 6;
                if (name.includes('46mm')) return 5;
                if (name.includes('45mm')) return 4;
                if (name.includes('44mm')) return 3;
                if (name.includes('42mm') || name.includes('43mm')) return 2;
                if (name.includes('40mm') || name.includes('41mm')) return 1;
                if (name.includes('38mm')) return 0;
                return 2;
            };
            
            const priorityA = getSizePriority(nameA);
            const priorityB = getSizePriority(nameB);
            
            if (priorityB !== priorityA) {
                return priorityB - priorityA;
            }
            
            return nameA.localeCompare(nameB);
        });
    }, [getWatchReleaseYear]);

    // Fetch watches from API based on brand
    useEffect(() => {
        const fetchWatches = async () => {
            setLoading(true);
            setError(null);
            try {
                const dbBrand = getWatchDbBrand(brandUrlParam);
                const response = await fetch('/api/watches');
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch watches: ${response.status}`);
                }
                
                const data = await response.json();
                
                // Filter watches by brand and map to expected structure
                const brandWatches = data
                    .filter(watch => watch.brand.toLowerCase() === dbBrand.toLowerCase())
                    .map(watch => ({
                        name: watch.model,
                        model: watch.model,
                        brand: watch.brand,
                        image: watch.image || '',
                        repairs: watch.repairs || []
                    }));
                
                // Sort watches by year (latest first)
                const sortedWatches = sortWatchesByYear(brandWatches);
                setModels(sortedWatches);
            } catch (err) {
                console.error('Error fetching watches:', err);
                setError(err.message || 'Failed to load watches');
                setModels([]);
            } finally {
                setLoading(false);
            }
        };

        if (brandUrlParam) {
            fetchWatches();
        }
    }, [brandUrlParam, getWatchDbBrand, sortWatchesByYear]);

    // Filter models based on search term
    const filteredModels = useMemo(() => {
        if (!searchTerm.trim()) return models;
        
        const searchLower = searchTerm.toLowerCase();
        const filteredModels = models.filter(model => 
            model.name.toLowerCase().includes(searchLower)
        );
        
        // Re-sort filtered results to maintain latest-to-oldest order
        return sortWatchesByYear(filteredModels);
    }, [models, searchTerm, sortWatchesByYear]);

    // Group Samsung watches by series
    const groupedModels = useMemo(() => {
        if (brandUrlParam?.toLowerCase() === 'samsung') {
            const groups = {
                'Galaxy Watch': [],
                'Galaxy Watch Classic': [],
                'Galaxy Watch Active': [],
                'Others': []
            };
            
            const modelsToGroup = searchTerm.trim() 
                ? models.filter(model => model.name.toLowerCase().includes(searchTerm.toLowerCase()))
                : models;
                
            modelsToGroup.forEach(model => {
                const lowerName = model.name.toLowerCase();
                if (lowerName.includes('watch classic')) {
                    groups['Galaxy Watch Classic'].push(model);
                } else if (lowerName.includes('watch active')) {
                    groups['Galaxy Watch Active'].push(model);
                } else if (lowerName.includes('galaxy watch')) {
                    groups['Galaxy Watch'].push(model);
                } else {
                    groups['Others'].push(model);
                }
            });
            
            // Sort each group from latest to oldest
            Object.keys(groups).forEach(groupName => {
                groups[groupName] = sortWatchesByYear(groups[groupName]);
            });
            
            return groups;
        }
        return null;
    }, [brandUrlParam, models, searchTerm, sortWatchesByYear]);

    useEffect(() => {
        setSelectedWatch(null);
        setShowRepairsModal(false);
        setIsLoadingRepairs(false);
        setFetchError(null);
        setRepairsData([]);
        setSearchTerm('');
    }, [brandUrlParam]);

    const handleModelClick = useCallback(async (model) => {
        if (!model || isLoadingRepairs) return;
        setSelectedWatch(model);
        setIsLoadingRepairs(true);
        setFetchError(null);
        setRepairsData([]);
        setShowRepairsModal(true);
        
        try {
            // Use the repairs data that came with the watch
            if (model.repairs && model.repairs.length > 0) {
                setRepairsData(model.repairs);
            } else {
                // Fallback to API call if repairs not included
                const dbBrand = model.brand || getWatchDbBrand(brandUrlParam);
                const url = `/api/watches/repairs?brand=${dbBrand}&model=${encodeURIComponent(model.name)}`;
                console.log("Request URL:", url);
                const response = await fetch(url);
                if (!response.ok) throw new Error(`Server error: ${response.status} ${response.statusText}`);
                const data = await response.json();
                setRepairsData(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching watch repairs data:', error);
            setFetchError(error.message || 'Failed to load repair details.');
            setRepairsData([]);
        } finally {
            setIsLoadingRepairs(false);
        }
    }, [isLoadingRepairs, brandUrlParam, getWatchDbBrand]);

    const handleModelKeyDown = useCallback((event, model) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleModelClick(model);
        }
    }, [handleModelClick]);

    const handleCloseModal = useCallback(() => {
        setShowRepairsModal(false);
    }, []);

    // Check for model query parameter and auto-open modal
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const modelParam = params.get('model');
        
        if (modelParam && models.length > 0 && !isLoadingRepairs && !hasOpenedModalFromUrl) {
            setHasOpenedModalFromUrl(true);
            
            // Clear the query parameter immediately
            const newParams = new URLSearchParams(location.search);
            newParams.delete('model');
            const newSearch = newParams.toString();
            const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
            window.history.replaceState({}, '', newUrl);
            
            // Normalize the search query
            const normalizedParam = modelParam.toLowerCase().trim();
            
            // Find the matching model - EXACT MATCH ONLY
            const matchingModel = models.find(model => {
                const modelName = (model.name || model.model || '').toLowerCase().trim();
                
                // EXACT MATCH ONLY - case insensitive
                if (modelName === normalizedParam) {
                    console.log('✓ Exact match found:', modelName);
                    return true;
                }
                
                return false;
            });
            
            if (matchingModel) {
                console.log('Opening modal for watch:', matchingModel.name);
                setTimeout(() => {
                    handleModelClick(matchingModel);
                }, 100);
            } else {
                // Show "Device not found" message
                console.log('❌ No exact match found for:', normalizedParam);
                
                // Show error notification
                const notification = document.createElement('div');
                notification.style.cssText = `
                    position: fixed;
                    top: 20px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #dc3545;
                    color: white;
                    padding: 15px 25px;
                    border-radius: 8px;
                    z-index: 10000;
                    font-size: 14px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    animation: slideDown 0.3s ease;
                `;
                notification.innerHTML = `
                    <strong>❌ Device not found</strong><br>
                    "${modelParam}" is not available
                `;
                document.body.appendChild(notification);
                
                // Add animation
                const style = document.createElement('style');
                style.textContent = `
                    @keyframes slideDown {
                        from { transform: translate(-50%, -100%); opacity: 0; }
                        to { transform: translate(-50%, 0); opacity: 1; }
                    }
                `;
                document.head.appendChild(style);
                
                // Remove notification after 5 seconds
                setTimeout(() => {
                    notification.style.animation = 'slideDown 0.3s ease reverse';
                    setTimeout(() => {
                        document.body.removeChild(notification);
                        document.head.removeChild(style);
                    }, 300);
                }, 5000);
            }
        }
    }, [models, location.search, handleModelClick, isLoadingRepairs, hasOpenedModalFromUrl]);

    const getBrandDisplayName = (brand) => {
        const brandMap = {
            'iwatch': 'Apple Watch',
            'samsung': 'Samsung Galaxy Watch',
            'garmin': 'Garmin',
            'fitbit': 'Fitbit',
            'amazfit': 'Amazfit'
        };
        return brandMap[brand?.toLowerCase()] || brand;
    };

    const brandDisplayName = getBrandDisplayName(brandUrlParam);

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p>Loading {brandDisplayName} models...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.errorContainer}>
                <p style={styles.errorText}>Error: {error}</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Select Your {brandDisplayName} Model</h1>
            </div>

            {/* Search Bar */}
            <div style={styles.searchContainer}>
                <FaSearch style={styles.searchIcon} />
                <input
                    type="text"
                    placeholder={`Search ${brandDisplayName} models...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />
            </div>

            {/* Results count */}
            {searchTerm && (
                <p style={styles.resultsCount}>
                    Found {filteredModels.length} model{filteredModels.length !== 1 ? 's' : ''}
                </p>
            )}

            {/* Models Grid */}
            {groupedModels ? (
                // Grouped display for Samsung
                <div style={styles.groupedContainer}>
                    {Object.entries(groupedModels).map(([groupName, groupModels]) => {
                        if (groupModels.length === 0) return null;
                        return (
                            <div key={groupName} style={styles.groupSection}>
                                <h2 style={styles.groupTitle}>{groupName}</h2>
                                <div style={styles.modelsGrid}>
                                    {groupModels.map((model, index) => (
                                        <div
                                            key={index}
                                            style={styles.modelCard}
                                            onClick={() => handleModelClick(model)}
                                            onKeyDown={(e) => handleModelKeyDown(e, model)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-5px)';
                                                e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.3)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                            tabIndex={0}
                                            role="button"
                                            aria-label={`Select ${model.name} for repairs`}
                                        >
                                            <img
                                                src={model.image || '/images/watch-placeholder.png'}
                                                alt={model.name}
                                                style={styles.modelImage}
                                                onError={(e) => {
                                                    e.target.src = '/images/watch-placeholder.png';
                                                }}
                                            />
                                            <p style={styles.modelName}>{model.name}</p>
                                            <div style={styles.repairIcon}>
                                                <FaTools />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                // Regular grid for other brands
                <div style={styles.modelsGrid}>
                    {filteredModels.length > 0 ? (
                        filteredModels.map((model, index) => (
                            <div
                                key={index}
                                style={styles.modelCard}
                                onClick={() => handleModelClick(model)}
                                onKeyDown={(e) => handleModelKeyDown(e, model)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-5px)';
                                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                                tabIndex={0}
                                role="button"
                                aria-label={`Select ${model.name} for repairs`}
                            >
                                <img
                                    src={model.image || '/images/watch-placeholder.png'}
                                    alt={model.name}
                                    style={styles.modelImage}
                                    onError={(e) => {
                                        e.target.src = '/images/watch-placeholder.png';
                                    }}
                                />
                                <p style={styles.modelName}>{model.name}</p>
                                <div style={styles.repairIcon}>
                                    <FaTools />
                                </div>
                            </div>
                        ))
                    ) : (
                        <p style={styles.noModelsText}>
                            {searchTerm 
                                ? `No ${brandDisplayName} models found matching "${searchTerm}"`
                                : `No ${brandDisplayName} models available`}
                        </p>
                    )}
                </div>
            )}

            {/* Repairs Modal */}
            {showRepairsModal && (
                <RepairsModal
                    show={showRepairsModal}
                    onClose={handleCloseModal}
                    watchModel={{
                        name: selectedWatch?.name || 'Device',
                        repairs: repairsData,
                        image: selectedWatch?.image
                    }}
                />
            )}
        </div>
    );
}

// Styles - Black and White Theme
const styles = {
    container: {
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa'
    },
    header: {
        marginBottom: '30px',
        textAlign: 'center'
    },
    backButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: '#6c757d',
        textDecoration: 'none',
        fontSize: '16px',
        marginBottom: '20px',
        transition: 'opacity 0.3s',
        '&:hover': {
            opacity: '0.7'
        }
    },
    title: {
        fontSize: '2.5em',
        fontWeight: 'bold',
        color: '#6c757d',
        margin: '0'
    },
    searchContainer: {
        position: 'relative',
        maxWidth: '500px',
        margin: '0 auto 30px',
    },
    searchIcon: {
        position: 'absolute',
        left: '15px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#6c757d',
        fontSize: '18px'
    },
    searchInput: {
        width: '100%',
        padding: '12px 20px 12px 45px',
        fontSize: '16px',
        border: '2px solid #6c757d',
        borderRadius: '25px',
        outline: 'none',
        transition: 'border-color 0.3s',
        backgroundColor: '#fff',
        color: '#6c757d'
    },
    resultsCount: {
        textAlign: 'center',
        color: '#6c757d',
        marginBottom: '20px',
        fontSize: '14px'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '3px solid #ddd',
        borderTop: '3px solid #6c757d',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    errorContainer: {
        textAlign: 'center',
        padding: '40px',
        minHeight: '400px'
    },
    errorText: {
        color: '#6c757d',
        fontSize: '18px',
        marginBottom: '20px'
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: '#6c757d',
        textDecoration: 'none',
        fontSize: '16px',
        '&:hover': {
            opacity: '0.7'
        }
    },
    groupedContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '40px'
    },
    groupSection: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        padding: '20px',
        border: '2px solid #6c757d',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
    },
    groupTitle: {
        fontSize: '1.8em',
        fontWeight: '600',
        color: '#6c757d',
        marginBottom: '20px',
        paddingBottom: '10px',
        borderBottom: '2px solid #6c757d'
    },
    modelsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '20px',
        justifyContent: 'center'
    },
    modelCard: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        padding: '15px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '2px solid #6c757d',
        position: 'relative',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
        }
    },
    modelImage: {
        width: '100px',
        height: '100px',
        objectFit: 'contain',
        marginBottom: '10px'
    },
    modelName: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#6c757d',
        margin: '10px 0 5px'
    },
    repairIcon: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        backgroundColor: '#6c757d',
        color: '#fff',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        opacity: '0.9'
    },
    noModelsText: {
        textAlign: 'center',
        color: '#6c757d',
        fontSize: '18px',
        gridColumn: '1 / -1',
        padding: '40px'
    }
};

// Add keyframes for spinner animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;
document.head.appendChild(styleSheet);

export default WatchModels;